import { LitElement, html, css } from 'lit';
import { auth } from '../auth.js';
import './admin-recorder-settings.js';
import './admin-model-settings.js';
import './admin-required-fields.js';
import './admin-codelists.js';

/**
 * Sysadmin settings hub (/admin/settings/*). Left submenu + content pane.
 * Tabs: Recorder (recording-instructions default), Model & prompt (production
 * AI config), Verplichte velden and Codelijsten. The active tab comes from the route
 * (`tab` property) and clicking a submenu item navigates so the URL stays in sync.
 */
export class AdminGlobalSettings extends LitElement {
  static properties = {
    tab: { type: String },
    /** Codelijst-domein uit /admin/settings/codelists/:id; leeg = overzicht. */
    codelistDomain: { type: String },
  };

  constructor() {
    super();
    this.tab = 'recorder';
    this.codelistDomain = null;
    this._items = [
      { key: 'recorder', label: 'Recorder', path: '/admin/settings/recorder' },
      { key: 'model', label: 'AI & prompts', path: '/admin/settings/model' },
      { key: 'required-fields', label: 'Verplichte velden', path: '/admin/settings/required-fields' },
      { key: 'codelists', label: 'Codelijsten', path: '/admin/settings/codelists' },
    ];
  }

  _go(path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  _renderContent() {
    switch (this.tab) {
      case 'model':
        return html`<admin-model-settings></admin-model-settings>`;
      case 'required-fields':
        return html`<admin-required-fields></admin-required-fields>`;
      case 'codelists':
        return html`<admin-codelists .domainId=${this.codelistDomain}></admin-codelists>`;
      case 'recorder':
      default:
        return html`<admin-recorder-settings></admin-recorder-settings>`;
    }
  }

  render() {
    if (!auth.isSystemAdmin()) {
      return html`<div class="empty"><h1>Geen toegang</h1><p>Deze pagina is alleen voor systeembeheerders.</p></div>`;
    }
    return html`
      <div class="layout">
        <aside class="submenu">
          <div class="submenu-title">Instellingen</div>
          ${this._items.map(item => html`
            <button
              class="submenu-item ${this.tab === item.key ? 'active' : ''}"
              @click=${() => this._go(item.path)}>${item.label}</button>
          `)}
        </aside>
        <section class="content">
          ${this._renderContent()}
        </section>
      </div>
    `;
  }

  static styles = css`
    :host { display: block; }
    .empty { max-width: 820px; }
    .empty h1 { margin: 0 0 0.35rem; font-size: 1.5rem; font-weight: 700; color: var(--mv-text); }
    .empty p { color: var(--mv-text-muted); }
    .layout { display: flex; gap: 2rem; align-items: flex-start; }
    .submenu {
      flex: 0 0 200px; display: flex; flex-direction: column; gap: 2px;
      position: sticky; top: 0;
    }
    .submenu-title {
      font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
      color: var(--mv-text-muted); padding: 6px 12px; margin-bottom: 4px;
    }
    .submenu-item {
      text-align: left; background: transparent; border: none; cursor: pointer;
      padding: 9px 12px; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
      color: var(--mv-text);
    }
    .submenu-item:hover { background: var(--mv-surface-alt, rgba(0,0,0,0.04)); }
    .submenu-item.active { background: var(--mv-primary); color: #fff; }
    .content { flex: 1 1 auto; min-width: 0; }
    @media (max-width: 720px) {
      .layout { flex-direction: column; gap: 1rem; }
      .submenu { flex-basis: auto; position: static; flex-direction: row; flex-wrap: wrap; }
    }
  `;
}

customElements.define('admin-global-settings', AdminGlobalSettings);
